import '../../App.css';
import '../../assets/css/Incident.css';
import { useEffect, useMemo, useState } from 'react';
import { getIncidentData, addComment, sendEmail, getAttachments, generateSummary } from '../../utils/AWSAPI';
import LoadingSpinner from '../../utils/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import i18n from "../Translations/i18n/i18n";
import { getFileFromBase64, serializeFile, submitAttachments } from '../../utils/Utils';
import DOMPurify from 'dompurify';

import { IncidentModal } from '../../components/IncidentModal';
import { validateFilesSnow, isHeicLike, ensureJpegIfHeic } from '../../utils/FileHelper';

function Incident() {
  // DATA API
  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [attachments, setAttachments] = useState(null);

  // SUMMARY
  const [summary, setSummary] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  
  const [modal, setModal] = useState({
    isOpen: false,
    uncloseable: false,
    kind: /** @type {'message'|'update'|'cancel'} */ ('message'),
    title: 'Loading...',
    message: '',
  });

  
  const [updateForm, setUpdateForm] = useState({ comments: '' });

  
  const [attachmentDisplayName, setAttachmentDisplayName] = useState("");
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [fileState, setFileState] = useState({ isConverting: false });
  const [fileValidation, setFileValidation] = useState({
    status: "idle", // "idle" | "loading" | "success" | "failure"
    message: ""
  });

  
  const [cancelForm, setCancelForm] = useState({
    reason: '',
    comments: '',
  });

  const { t } = useTranslation();

  const closeModal = () => {
    if (modal.uncloseable) return;
    setModal((m) => ({ ...m, isOpen: false }));
  };

  const openMessageModal = (title, message, uncloseable = true) => {
    setModal({
      isOpen: true,
      uncloseable,
      kind: 'message',
      title,
      message,
    });
  };

  const openUpdateModal = (title) => {
    setUpdateForm({ comments: '' });
    setAttachmentDisplayName("");
    setAttachmentFile(null);
    setFileState({ isConverting: false });
    setFileValidation({ status: "idle", message: "" });
    setModal({
      isOpen: true,
      uncloseable: false,
      kind: 'update',
      title,
      message: '',
    });
  };

  const openCancelModal = (title) => {
    setCancelForm({ reason: '', comments: '' });
    setModal({
      isOpen: true,
      uncloseable: false,
      kind: 'cancel',
      title,
      message: '',
    });
  };

  const validateSingleAttachmentSnow = async (file) => {
    if (!file) {
      setFileValidation({ status: "idle", message: "" });
      return;
    }

    setFileValidation({ status: "loading", message: "" });

    try {
      const res = await validateFilesSnow([file]);
      const results = res?.Result?.result || [];

      if (!Array.isArray(results) || results.length === 0) {
        setFileValidation({ status: "failure", message: "Validation failed: empty response." });
        return;
      }

      const r = results[0];
      if (r?.Status === "Success") {
        setFileValidation({ status: "success", message: "" });
        return;
      }

      setFileValidation({
        status: "failure",
        message: r?.Message || "Invalid file."
      });
    } catch (e) {
      setFileValidation({ status: "failure", message: "Validation request failed." });
    }
  };

  const onAttachmentChange = async (e) => {
    const rawFile = e.target.files?.[0] || null;
    setFileState({ isConverting: false });

    if (!rawFile) {
      setAttachmentFile(null);
      setAttachmentDisplayName("");
      setFileValidation({ status: "idle", message: "" });
      return;
    }

    try {
      const heic = isHeicLike(rawFile);
      if (heic) setFileState({ isConverting: true });

      const finalFile = await ensureJpegIfHeic(rawFile);

      if (heic) setFileState({ isConverting: false });

      setAttachmentFile(finalFile);
      setAttachmentDisplayName(finalFile?.name || "");

      await validateSingleAttachmentSnow(finalFile);
    } catch (err) {
      console.error("Attachment process error:", err);

      setFileState({ isConverting: false });
      setAttachmentDisplayName("");
      setAttachmentFile(null);

      if (err?.message?.includes("Image is already browser readable")) {
        setFileValidation({
          status: "failure",
          message:
            "The file content doesn’t match its extension. It may have been renamed or corrupted. Please upload a valid file."
        });
        return;
      }

      let errorMessage = "Failed to process attachment.";
      if (err?.message) errorMessage = err.message;
      else if (typeof err === "string") errorMessage = err;

      setFileValidation({ status: "failure", message: errorMessage });
    } finally {
      // allow re-selecting same file
      e.target.value = "";
    }
  };
const clearAttachment = () => {
  setAttachmentFile(null);
  setAttachmentDisplayName("");
  setFileState({ isConverting: false });
  setFileValidation({ status: "idle", message: "" });
};
  const canSubmitUpdate = useMemo(() => {
    if (!attachmentFile) return true;
    if (fileState.isConverting) return false;
    if (fileValidation.status === "loading") return false;
    if (fileValidation.status === "failure") return false;
    return fileValidation.status === "success";
  }, [attachmentFile, fileState.isConverting, fileValidation.status]);

  const setDefaultAttributes = async () => {
    const url = new URL(window.location.href);
    const ticketNumberValue = url.searchParams.get('ticketNumber');

    if (!ticketNumberValue) {
      openMessageModal(t('whoops'), t('no-IncidentsFound'));
      return { success: false };
    }
    return { success: true, ticketNumber: ticketNumberValue };
  };

  const loadSummary = async (caseNumber) => {
    try {
      setIsSummaryLoading(true);
      setSummaryError(null);
      const summaryData = await generateSummary(caseNumber, localStorage.getItem("language"));

      if (summaryData.Error || summaryData.message) {
        setSummaryError(summaryData.Error || summaryData.message);
      } else {
        setSummary(summaryData);
      }
    } catch (error) {
      console.error('Error generating summary:', error);
      setSummaryError('Failed to generate summary.');
    } finally {
      setIsSummaryLoading(false);
    }
  };

  useEffect(() => {
    const loadLanguage = () => {
      const language = localStorage.getItem("language");
      if (language) {
        i18n.changeLanguage(language);
      }
    };
    loadLanguage();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { success, ticketNumber: fetchedTicketNumber } = await setDefaultAttributes();
        if (!success) return;

        const employeeId = localStorage.getItem("user_identifier") || "";
        const incidentData = await getIncidentData(fetchedTicketNumber, employeeId);

        if (incidentData.Error) {
          openMessageModal(t('error'), incidentData.Error);
          return;
        }

        setData(incidentData);
        loadSummary(incidentData.CaseNumber);

        const attachmentData = await getAttachments(incidentData.SysID, "incident");
        if (!attachmentData.Error) setAttachments(attachmentData);

        setLoading(false);
      } catch (err) {
        console.log(err);
      }
    };

    if (isLoading) fetchData();
  }, [isLoading]);

  const updateAndAddComment = async (comment, singleAttachmentFile) => {
    try {
      setLoading(true);
      closeModal();

      const commentRes = await addComment(data.CaseNumber, data.SysID, comment);

      if (commentRes.Error) {
        setLoading(false);
        openMessageModal(t('error'), commentRes.Error);
        return;
      }

      const filesToUpload = singleAttachmentFile ? [singleAttachmentFile] : [];

      const serializedFiles = await Promise.all(
        filesToUpload.map(async (f) => await serializeFile(f))
      );

      const attachRes = await submitAttachments(data.SysID, serializedFiles);

      const hasAttachmentError = Boolean(attachRes?.Error);

      let modalText = t('successfullyAddedComment') + commentRes.CaseNumber + "\n\n";

      if (hasAttachmentError) {
        const details = attachRes.Detail || attachRes.Message || attachRes.Error || "Unknown error";
        modalText +=
          t('Failed to upload attachments') +
          "\n\n" +
          t('Details') +
          ": " +
          details;
      }

      openMessageModal(t('success'), modalText);

      setLoading(false);

      const redirectDelayMs = hasAttachmentError ? 10000 : 1000;

      setTimeout(() => {
        window.location.href = `incident?ticketNumber=${commentRes.CaseNumber}`;
      }, redirectDelayMs);
    } catch (err) {
      console.log(err);
    }
  };

  const updateAndRequestCancellation = async (reason, comments) => {
    try {
      setLoading(true);
      closeModal();

      const table = `
        <table border="1">
          <thead>
            <tr>
              <th>${t('cancellationFor')}</th>
              <th>${t('reason')}</th>
              <th>${t('comments')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Steven Paniagua</td>
              <td>${reason}</td>
              <td>${comments}</td>
            </tr>
          </tbody>
        </table>
      `;

      const emailSubject = t('cancellationRequest') + data.CaseNumber;
      const emailBody = table;
      const emailAddresses = ['steven.paniagua@fujitsu.com'];

      const email = await sendEmail(emailSubject, emailBody, emailAddresses);
      const comment = await addComment(
        data.CaseNumber,
        data.SysID,
        (t('Requested a cancellation')) + "\n" +
          t('reason') + "\n" + reason + "\n" +
          t('additionalComments') + "\n" + comments
      );

      setLoading(false);

      if (email.Error || comment.Error) {
        openMessageModal(t('error'), email.Error || comment.Error);
        return;
      }

      openMessageModal(t('success'), t('Successfully requested a cancellation for: ') + data.CaseNumber);

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.log(err);
    }
  };

  function openAttachment(attachment) {
    const file = getFileFromBase64(
      attachment.base64EncodedData,
      attachment.file_name,
      attachment.content_type
    );
    const blobUrl = URL.createObjectURL(file);
    window.open(blobUrl, '_blank');
  }

  const isClosedLike = useMemo(() => {
    if (!data) return false;
    return ['Resolved', 'Cancelled', 'Closed'].includes(data.Status);
  }, [data]);

  return (
    <div className="App">
      <LoadingSpinner show={isLoading} />

      {data && (
        <div className='extra-padding'>
         <IncidentModal
  modal={modal}
  t={t}
 state={{
  updateForm,
  cancelForm,
  attachmentDisplayName,
  hasAttachment: Boolean(attachmentFile), 
  fileState,
  fileValidation,
  canSubmitUpdate,
}}
  actions={{
  closeModal,
  setUpdateComments: (v) => setUpdateForm((s) => ({ ...s, comments: v })),
  setCancelReason: (v) => setCancelForm((s) => ({ ...s, reason: v })),
  setCancelComments: (v) => setCancelForm((s) => ({ ...s, comments: v })),
  onAttachmentChange,
  clearAttachment, // 
  submitUpdate: () => updateAndAddComment(updateForm.comments || "Unspecified", attachmentFile),
  submitCancel: () =>
    updateAndRequestCancellation(cancelForm.reason, cancelForm.comments || "Unspecified"),
}}
/>
          

          {data.Active ? (
            <div className='incident-buttons-group'>
              <ul>
                {!isClosedLike ? (
                  <>
                    <a onClick={() => openUpdateModal("UPDATE INCIDENT")}>
                      <li className='incident-buttons'>{t('updateIncident')}</li>
                    </a>

                    <a onClick={() => openCancelModal("CANCEL INCIDENT")}>
                      <li className='incident-buttons'>{t('requestCancellation')}</li>
                    </a>
                  </>
                ) : (
                  <a onClick={() => openUpdateModal("RE-OPEN / ESCALATE")}>
                    <li className='incident-buttons'>{t('reOpen')}</li>
                  </a>
                )}
              </ul>
            </div>
          ) : (
            <div style={{ backgroundColor: 'rgb(238, 208, 38)', padding: '10px', color: 'black', fontWeight: 'bold' }}>
              {t('TicketNoLongerActive')}
            </div>
          )}

          <div className='tabla'>
            <div>
              <div className='columns'>
                <div className='align-column-left'>
                  <p>{t('incidentNumber')}</p>
                  <input className='input-column' value={data.CaseNumber} type="text" disabled />
                  <p>{t('status')}</p>
                  <input className='input-column' value={data.Status} type="text" disabled />
                  <p>{t('restaurant')}</p>
                  <input className='input-column' value={data.Location} type="text" disabled />
                </div>
                <div className='align-column-center hide'>
                  <p>{t('category')}</p>
                  <input className='input-column' value={data.Category} type="text" disabled />
                  <p>{t('subcategory')}</p>
                  <input className='input-column' value={data.Subcategory} type="text" disabled />
                </div>
                <div className='align-column-right'>
                  <p>{t('ageOfTicket')}</p>
                  <input className='input-column' value={data.AgeOfTicket} type="text" disabled />
                  <p>{t('assignmentGroupName')}</p>
                  <input className='input-column' value={data.AssignmentGroup} type="text" disabled />
                  <p>{t('openedBy')}</p>
                  <input className='input-column' value={data.OpenedBy} type="text" disabled />
                </div>
              </div>

              <div className='short-text'>
                <p>{t('shortDescription')}</p>
                <input className='short-input' value={data.ShortDescription} type="text" disabled />
              </div>

              <div className='full-text'>
                <p>{t('fullDescription')}</p>
                <textarea className='full-input' value={data.FullDescription} rows="10" disabled />
              </div>

              <div className='full-text'>
                <p>AI Generated Summary</p>
                {isSummaryLoading ? (
                  <div className='full-input' style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100px', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                    <span>Generating AI summary...</span>
                  </div>
                ) : summaryError ? (
                  <textarea className='full-input' value={`Error: ${summaryError}`} rows="3" disabled />
                ) : summary ? (
                  <textarea className='full-input' value={summary.summary} rows="3" disabled />
                ) : (
                  <textarea className='full-input' value="No summary available." rows="3" disabled />
                )}
              </div>

              {(data.Comments && data.Comments.length > 0) || data.CloseNotes ? (
                <div className='full-text'>
                  <p>{t('comments')}</p>

                  {data.CloseNotes && (
                    <div className='comment-box'>
                      <div className='bookmark' style={{ backgroundColor: "rgba(236, 179, 63, 1)0, 143)" }}></div>
                      <div className='details'>
                        <div className='sender'>Resolved by {data.ResolvedBy || ''}</div>
                        <div className='date close-note-date'>{data.ResolvedAt || ''}</div>
                      </div>
                      <p>{data.CloseNotes}</p>
                    </div>
                  )}

                  {data.Comments.map((comment, index) => (
                    <div key={index} className='comment-box'>
                      <div className='bookmark' style={{ backgroundColor: "rgb(22, 70, 143)" }}></div>
                      <div className='details'>
                        <div className='sender'>{comment.created_by}</div>
                        <div className='date'>{comment.created}</div>
                      </div>
                      {(() => {
                        let sanitizedContent = DOMPurify.sanitize(
                          (comment.comments || "").replace(/\[code\]|\[\/code\]/g, '')
                        );
                        return <p dangerouslySetInnerHTML={{ __html: sanitizedContent }} />;
                      })()}
                    </div>
                  ))}
                </div>
              ) : (
                <div className='full-text'>{t('No comments found.')}</div>
              )}

              {attachments && attachments.length > 0 ? (
                <div className='full-text'>
                  <p>{t('Attachments')}</p>
                  {attachments.map((attachment, index) => (
                    <div key={index} className='comment-box'>
                      <div className='bookmark' style={{ backgroundColor: "rgb(22, 70, 143)" }}></div>
                      <div className='details'>
                        <div className='sender'>{attachment.sys_created_by}</div>
                        <div className='attachment-date'>{attachment.sys_created_on}</div>
                      </div>

                      {attachment.base64EncodedData && attachment.file_name && attachment.content_type && (
                        <div>
                          <a href="#" onClick={() => openAttachment(attachment)}>{t('View Attachment')}</a>
                          <a
                            href={URL.createObjectURL(
                              getFileFromBase64(attachment.base64EncodedData, attachment.file_name, attachment.content_type)
                            )}
                            download
                          >
                            {t('Download Attachment')}
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className='full-text'>{t('No attachments found.')}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Incident;